import type {
  ContractId,
  MissionStage,
  RunContractState,
} from "./contracts.js";
import type { CargoKind } from "./risk.js";
import { canCollectCargo } from "./cargo-stacks.js";
import {
  FACTION_LOCATIONS,
  FACTION_SECTOR_IDS,
  type FactionSectorId,
} from "./world-layout.js";

export const CONTRACT_DIFFICULTIES = ["standard", "advanced"] as const;
export type ContractDifficulty = (typeof CONTRACT_DIFFICULTIES)[number];
export const CUSTOMER_IDS = ["security", "research", "logistics"] as const;
export type CustomerId = (typeof CUSTOMER_IDS)[number];
export type Reputation = Record<CustomerId, number>;
export const CUSTOMERS: Record<
  CustomerId,
  { name: string; contractId: Exclude<ContractId, "intro"> }
> = {
  security: { name: "Служба безопасности", contractId: "hunt" },
  research: { name: "Картографический союз", contractId: "salvage" },
  logistics: { name: "Независимые перевозчики", contractId: "signal" },
};
export const ADVANCED_REPUTATION = 20;
export const WRECKS_SURVEY_SITE_IDS = [
  "wrecks-north",
  "wrecks-south",
  "wrecks-east",
] as const;
type TerritorySurveySiteId = `${FactionSectorId}-scan-${1 | 2 | 3}`;
const territorySurveyIds = FACTION_SECTOR_IDS.flatMap(
  (id): TerritorySurveySiteId[] => [
    `${id}-scan-1`,
    `${id}-scan-2`,
    `${id}-scan-3`,
  ],
);
export const SURVEY_SITE_IDS = [
  ...WRECKS_SURVEY_SITE_IDS,
  ...territorySurveyIds,
] as const;
export type SurveySiteId = (typeof SURVEY_SITE_IDS)[number];
export interface SurveySiteDefinition {
  id: SurveySiteId;
  name: string;
  x: number;
  y: number;
}
export const WRECKS_SURVEY_SITES: readonly SurveySiteDefinition[] = [
  { id: "wrecks-north", name: "Северный скан-маяк", x: 4400, y: 450 },
  { id: "wrecks-south", name: "Южный скан-маяк", x: 5180, y: 1760 },
  { id: "wrecks-east", name: "Восточный скан-маяк", x: 5950, y: 1430 },
];
export const SURVEY_SITES: readonly SurveySiteDefinition[] = [
  ...WRECKS_SURVEY_SITES,
  ...FACTION_LOCATIONS.flatMap((location) =>
    ([1, 2, 3] as const).map((ordinal): SurveySiteDefinition => {
      const point = location.surveyPoints[ordinal - 1];
      if (!point)
        throw new Error(`Missing survey point ${location.id}/${ordinal}`);
      return {
        id: `${location.id}-scan-${ordinal}`,
        name: `${location.name}: скан-маяк ${ordinal}`,
        ...point,
      };
    }),
  ),
];
export const SURVEY_RADIUS = 110;
export interface ReputationAward {
  customerId: CustomerId;
  amount: number;
  balance: number;
}
export const ADVANCED_CONTRACTS = {
  hunt: {
    title: "Обезглавить патруль",
    description:
      "Четыре боевых зачёта, включая командира патруля. Обычных налётчиков недостаточно.",
    goal: 4,
    credits: "250",
    components: 6,
  },
  salvage: {
    title: "Картография обломков",
    description:
      "Обследуйте три скан-маяка в поле обломков и привезите два контейнера оборудования.",
    goal: 2,
    credits: "180",
    components: 5,
  },
  signal: {
    title: "Вывести «Вегу»",
    description:
      "Привезите две энергии и лично помогите спасти транспорт: урон пиратам или реальный ремонт.",
    goal: 2,
    credits: "100",
    components: 4,
  },
  escort: {
    title: "Ответственное сопровождение",
    description:
      "Пройди рядом с транспортом не менее 80% пути и внеси 25 реального урона или ремонта, затем доведи его до конца маршрута.",
    goal: 1,
    credits: "250",
    components: 5,
  },
};
export function emptyReputation(): Reputation {
  return { security: 0, research: 0, logistics: 0 };
}
export function contractCustomer(contractId: ContractId): CustomerId | null {
  return contractId === "hunt"
    ? "security"
    : contractId === "salvage"
      ? "research"
      : contractId === "signal" || contractId === "escort"
        ? "logistics"
        : null;
}
export function contractReputation(difficulty: ContractDifficulty): number {
  return difficulty === "advanced" ? 15 : 10;
}
export function contractObjectivesComplete(
  contract: RunContractState,
): boolean {
  const terms = contract.terms;
  return (
    (!terms.escort || contract.escort?.phase === "arrived") &&
    (!terms.requireElite || contract.eliteKills > 0) &&
    (!terms.requireRescue || contract.rescue === "saved") &&
    terms.requiredSurveySites.every((id) =>
      contract.surveyedSites.includes(id),
    ) &&
    terms.requiredArchetypes.every((id) =>
      contract.killedArchetypes.includes(id),
    )
  );
}
export function contractComplete(contract: RunContractState): boolean {
  return (
    contract.progress >= contract.goal &&
    contract.cargoHeld >= contract.terms.cargoGoal &&
    contractObjectivesComplete(contract)
  );
}
export function contractStage(
  contract: RunContractState,
  cargo: readonly CargoKind[],
  capacity: number,
  hasSalvage: boolean,
): MissionStage {
  if (contract.stage === "settled") return "settled";
  if (contract.contractId === "escort")
    return contract.escort?.phase === "arrived" ||
      contract.escort?.phase === "lost"
      ? "return"
      : contract.escort
        ? "engage"
        : "outbound";
  const holdBlocked = !canCollectCargo(cargo, capacity, contract.terms.cargoKind);
  if (contractObjectivesComplete(contract) === false) {
    if (contract.terms.requireRescue && contract.rescue === "lost")
      return "return";
    if (
      contract.terms.cargoGoal > 0 &&
      holdBlocked &&
      contract.cargoHeld < contract.terms.cargoGoal
    )
      return "return";
    return contract.contractId === "hunt" ? "engage" : "collect";
  }
  if (
    contractComplete(contract) ||
    holdBlocked ||
    (contract.contractId === "signal" &&
      contract.terms.cargoGoal > 0 &&
      hasSalvage)
  )
    return "return";
  return contract.progress > 0
    ? contract.contractId === "hunt"
      ? "engage"
      : "collect"
    : "outbound";
}
